import React, { useState } from 'react';
import { Modal, Form, Select, Input, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../../api/config/axiosConfig';

const { TextArea } = Input;

const REASON_KEYS = [
  { value: 'FAKE_LISTING',      key: 'fake_listing' },
  { value: 'WRONG_CATEGORY',    key: 'wrong_category' },
  { value: 'OFFENSIVE_CONTENT', key: 'offensive_content' },
  { value: 'ALREADY_SOLD',      key: 'already_sold' },
  { value: 'WRONG_PRICE',       key: 'wrong_price' },
  { value: 'SPAM',              key: 'spam' },
  { value: 'OTHER',             key: 'other' },
];

interface ReportModalProps {
  open: boolean;
  listingId: string;
  listingTitle: string;
  onClose: () => void;
}

const ReportModal: React.FC<ReportModalProps> = ({ open, listingId, listingTitle, onClose }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values: { reason: string; description?: string }) => {
    setLoading(true);
    try {
      await axiosInstance.post(`/listings/${listingId}/report`, values);
      message.success(t('report_modal.success'));
      form.resetFields();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || t('report_modal.fail');
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('report_modal.title')}
      onCancel={onClose}
      footer={null}
      width={480}
      destroyOnClose
    >
      <p style={{ color: 'rgba(0,0,0,0.55)', fontSize: 13, marginBottom: 20 }}>
        {t('report_modal.reporting_label')} <strong>{listingTitle}</strong>
      </p>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item name="reason" label={t('report_modal.reason_label')} rules={[{ required: true, message: t('report_modal.reason_required') }]}>
          <Select placeholder={t('report_modal.reason_placeholder')}>
            {REASON_KEYS.map((r) => (
              <Select.Option key={r.value} value={r.value}>{t(`report_modal.reasons.${r.key}`)}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="description" label={t('report_modal.details_label')}>
          <TextArea rows={4} placeholder={t('report_modal.details_placeholder')} maxLength={1000} showCount style={{ padding: '8px 12px', resize: 'vertical' }} />
        </Form.Item>
        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1, height: 40, border: '1px solid #d9d9d9', borderRadius: 6,
              background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500,
              lineHeight: '40px', padding: 0, boxSizing: 'border-box',
            }}
          >
            {t('report_modal.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              flex: 1, height: 40, border: 'none', borderRadius: 6,
              background: loading ? '#fca07a' : '#fe5a1d', color: '#fff',
              cursor: loading ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600,
              lineHeight: '40px', padding: 0, boxSizing: 'border-box', opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? t('report_modal.submitting') : t('report_modal.submit')}
          </button>
        </div>
      </Form>
    </Modal>
  );
};

export default ReportModal;
